import {
  getInvitationsOrganizationsUnverifiedOptions,
  Invitation,
} from "@aura-frontend/omnibus-az-client";
import { useQuery } from "@tanstack/react-query";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { Button, Table } from "@win-reality/react-ui";
import { format } from "date-fns";
import { useMemo, useState } from "react";
import { create } from "zustand";

import { CheckIcon } from "@/components/icons/CheckIcon";
import { XIcon } from "@/components/icons/XIcon";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAppContext } from "@/hooks/use-app-context";

import ViewAccountLinkModal from "./components/ViewLinkModal";
import { ReviewAccountVerificationDialog } from "./ReviewAccountVerification/ReviewAccountVerificationDialog";

interface SelectedInvitationState {
  selectedInvitation: Invitation | null;
  setSelectedInvitation: (invitation: Invitation | null) => void;
}

export const useSelectedInvitationStore = create<SelectedInvitationState>(
  (set) => ({
    selectedInvitation: null,
    setSelectedInvitation: (invitation) =>
      set({ selectedInvitation: invitation }),
  }),
);

const AccountVerificationPage = () => {
  const { data, isLoading, isRefetching } = useQuery({
    ...getInvitationsOrganizationsUnverifiedOptions({}),
    select: (result) => result?.invitations ?? [],
  });

  const [sortingState, setSortingState] = useState<SortingState>([]);

  const selectedInvitation = useSelectedInvitationStore(
    (state) => state.selectedInvitation,
  );
  const setSelectedInvitation = useSelectedInvitationStore(
    (state) => state.setSelectedInvitation,
  );

  const columns: ColumnDef<Invitation>[] = useMemo(
    () => [
      {
        accessorKey: "accountName",
        header: "Account Name",
        cell: ({ row }) => {
          const org = row.original.organization;
          if (!org) {
            return "";
          }
          return `${org?.orgName}`;
        },
      },
      {
        accessorKey: "accountOwner",
        header: "Account Owner",
        cell: ({ row }) => {
          const owner = row.original.owner;
          if (!owner) {
            return "-";
          }
          return owner.emails?.[0] ?? "";
        },
      },
      {
        accessorKey: "createdDate",
        header: "Created Date",
        cell: ({ row }) => {
          const createdAt = row.original.created_at;
          if (!createdAt) {
            return "-";
          }
          return format(createdAt * 1000, "MM/dd/yy");
        },
      },
      {
        accessorKey: "createdBy",
        header: "Created By",
        cell: ({ row }) => {
          const creator = row.original.created_by;
          if (!creator) {
            return "";
          }
          return creator.emails?.[0] ?? "";
        },
      },
      {
        accessorKey: "status",
        header: "Claim Status",
        cell: ({ getValue }) => {
          const status = getValue<Invitation["status"]>();
          return (
            <div className="pl-8">
              {status === "user_claimed" && (
                <CheckIcon
                  data-testid="status-icon-user_claimed"
                  className="h-5 w-5 text-status-success"
                />
              )}
              {status === "user_not_claimed" && (
                <XIcon
                  data-testid="status-icon-user_not_claimed"
                  className="h-5 w-5 text-status-unknown"
                />
              )}
              {status === "token_expired" && (
                <XIcon
                  data-testid="status-icon-token_expired"
                  className="h-5 w-5 text-status-error"
                />
              )}
            </div>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        enableSorting: false,
        cell: ({ row }) => {
          return <ActionCell invitation={row.original as Invitation} />;
        },
      },
    ],
    [],
  );

  return (
    <div className="relative flex flex-col">
      <div className="mt-8">
        <Table
          enableHorizontalScroll
          data={data ?? []}
          columns={columns}
          sortingState={sortingState}
          onSortingChange={setSortingState}
          onEndReached={() => {}}
          isFetching={isLoading || isRefetching}
          rowClassName={"shadow-glow-tiny rounded"}
          rowsWrapperClassName="gap-y-4"
          tableWrapperClassName="overflow-auto px-5 py-1"
          paginationClassName="px-5"
        />
      </div>

      <ReviewAccountVerificationDialog
        isOpen={!!selectedInvitation}
        organization={selectedInvitation?.organization}
        owner={selectedInvitation?.owner}
        onClose={() => setSelectedInvitation(null)}
      />
    </div>
  );
};

const ActionCell = ({ invitation }: { invitation: Invitation }) => {
  const [isViewLinkModalOpen, setViewLinkModalOpen] = useState(false);
  const setSelectedInvitation = useSelectedInvitationStore(
    (state) => state.setSelectedInvitation,
  );
  const { status, token } = invitation;
  const { user } = useAppContext();

  if (!status) {
    return "";
  }

  if (status === "user_claimed") {
    if (!invitation.organization || !invitation.owner || !invitation.created_by)
      return "";
    return (
      <TooltipProvider>
        <Tooltip
          delayDuration={0}
          open={
            invitation.created_by.userId !== user!.userId ? false : undefined
          }
        >
          <TooltipTrigger className="cursor-default">
            <Button
              variant="secondary-flat-light"
              isDisabled={invitation.created_by.userId === user!.userId}
              onClick={() => setSelectedInvitation(invitation)}
            >
              Review
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            You cannot verify an account that you invited.
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <div className="flex space-x-2">
      {status === "user_not_claimed" && (
        <>
          <ViewAccountLinkModal
            link={`${window.location.origin}/claim-permissions/?token=${token}`}
            open={isViewLinkModalOpen}
            onOpenChange={setViewLinkModalOpen}
          />
          <Button
            variant="secondary-flat-light"
            onClick={() => {
              setViewLinkModalOpen(true);
            }}
          >
            View link
          </Button>
        </>
      )}
      {
        //TODO: we still didn't decide how the new link should be handled
        //(update existing, generating another, extending lifetime etc)
      }
      {status === "token_expired" && (
        <>
          <ViewAccountLinkModal
            link={`${window.location.origin}/claim-permissions/?token=${token}`}
            open={isViewLinkModalOpen}
            onOpenChange={setViewLinkModalOpen}
          />
          <Button variant="secondary-flat-light" onClick={() => {}}>
            Generate new link
          </Button>
        </>
      )}
    </div>
  );
};

export default AccountVerificationPage;
